// Finger Beam fingertip charge + release (owner 2026-11-03: 'for finger blast to show energy charging at the fingertip where
// the beam is released from'). Render-only: it reads Sim state (sim.fingerNextIn(): seconds until the sim's next shot is due)
// and the sim's 'finger' events; it never decides when a shot fires and never delays damage.
//
//  - CHARGE: during the last CHARGE_T s before a shot, a round glowing orb swells at the hero's fingertip (white-hot core,
//    wide crimson halo) with a few faint sharp spike rays turning slowly through it and sparks drawn in. Between the beams
//    of a rank-4+ volley (0.1 s apart) it stays half-charged, so a barrage reads as one held point that keeps firing.
//  - RELEASE: the orb pops and a sharp 6-point starburst (8 for Scarlet Fan) flashes at the fingertip; the needle
//    (fingerBeam.ts) then streaks out from that same point (view.ts passes the fingertip as the needle's start).
//  - WASH (big moments only: the final beam of a max-rank volley and of every Scarlet Fan volley): a brief, subtle crimson
//    colour wash over the whole frame (main.ts reads `wash` into the post pipeline), with shadows picking up more of it.
//    It is one big flash in the Reduce flashing budget (FLASH.big()); Reduced keeps a quarter of it, Minimal none.
//
// Cost: one InstancedMesh of camera-facing quads (CAP instances, ≤ ~6 live), one material, no lights, no textures; the
// gathering sparks go through the shared particle pool (so they follow the particle density setting). Built up front with
// the view, so the boot / Start Run warm-up compiles it (prewarm.ts forceAll covers every instanced pool in the scene).
import * as THREE from 'three/webgpu';
import { abs, atan, attribute, clamp, cos, exp, float, length, max, mix, pow, smoothstep, uv, vec3 } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import { FLASH, SKILL, type Fx } from './fx';

/** seconds of visible charge before a shot (the sim's own timing; the charge never delays it) */
export const CHARGE_T = 0.2;
/** release starburst / orb-pop life (s) */
const STAR_LIFE = 0.11, POP_LIFE = 0.09;
/** colour wash: rise / total life (s) and peak strength (0..1, before Reduce flashing) */
export const WASH_RISE = 0.035, WASH_LIFE = 0.24, WASH_PEAK = 0.42;
const CAP = 12;

// Shatterlight's Finger Beam palette (fingerBeam.ts: hot 0xff1434, tail 0xd00a24, muzzle 0xff6a7c)
const HALO = new THREE.Color(0xff1a3c), STAR = new THREE.Color(0xff5a70);
/** wash tint (linear multiplier for the frame, and the colour lifted into the shadows) */
export const WASH_TINT = new THREE.Color(0xff2a48);

interface Item { kind: 0 | 1; x: number; y: number; z: number; t: number; life: number; s0: number; s1: number; rot: number; n: number; k: number; on: boolean }

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _rq = new THREE.Quaternion(), _z = new THREE.Vector3(0, 0, 1);

export class FingerChargeFx {
  readonly mesh: THREE.InstancedMesh;
  private col: THREE.InstancedBufferAttribute; private shp: THREE.InstancedBufferAttribute;
  private items: Item[] = [];
  /** current charge 0..1 (drawn) */
  charge = 0;
  /** full-screen wash strength 0..1 this frame (main.ts → post pipeline) */
  wash = 0;
  /** the post pipeline's wash uniform (set by main.ts; null in headless / QA hosts without it) */
  washU: { value: number } | null = null;
  readonly washTint = WASH_TINT;
  private washT = -1; private washK = 0;
  private spin = 0; private sparkAcc = 0;
  /** QA: force the charge level (0..1); null = follow the sim */
  holdCharge: number | null = null;
  /** QA: freeze release items / wash at this age (s); null = run normally */
  hold: number | null = null;
  /** QA counters: shots released, washes fired */
  stats = { released: 0, washes: 0 };

  constructor(scene: THREE.Scene) {
    for (let i = 0; i < CAP; i++) this.items.push({ kind: 0, x: 0, y: 0, z: 0, t: 9, life: 1, s0: 1, s1: 1, rot: 0, n: 6, k: 1, on: false });
    this.col = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 4), 4).setUsage(THREE.DynamicDrawUsage);
    this.shp = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 4), 4).setUsage(THREE.DynamicDrawUsage);
    const geo = new THREE.PlaneGeometry(1, 1);
    geo.setAttribute('fcCol', this.col); geo.setAttribute('fcShape', this.shp);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false });
    m.name = 'fingerCharge';
    const c = attribute('fcCol', 'vec4'), s = attribute('fcShape', 'vec4'); // c: halo rgb + strength; s: orb weight, star weight, rotation, spikes
    const p = uv().sub(0.5).mul(2), r = length(p);
    const ang = atan(p.y, p.x).add(s.z);
    // orb: a hard white-hot core and a wide soft halo (round, not spiky)
    const core = smoothstep(0.3, 0.06, r), halo = exp(r.mul(r).mul(-7)).mul(smoothstep(1.0, 0.7, r));
    // starburst: thin sharp rays (|cos(n·a/2)|^k), long and tapering, plus a short cross-glint at the centre
    const ray = pow(abs(cos(ang.mul(s.w).mul(0.5))), float(48)).mul(pow(max(float(0), float(1).sub(r)), float(1.6)));
    const starCore = smoothstep(0.22, 0.0, r);
    const orbI = core.mul(1.6).add(halo.mul(0.9)).mul(s.x), starI = ray.mul(1.5).add(starCore.mul(0.8)).mul(s.y);
    const white = clamp(core.mul(s.x).add(starCore.mul(s.y)).add(ray.mul(s.y).mul(pow(max(float(0), float(1).sub(r.mul(1.8))), float(2)))), 0, 1);
    m.colorNode = mix(vec3(c.x, c.y, c.z), vec3(1, 0.96, 0.97), white).mul(orbI.add(starI)).mul(c.w);
    this.mesh = new THREE.InstancedMesh(geo, m, CAP);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 7; this.mesh.name = 'fingerCharge';
    scene.add(this.mesh);
  }

  /** A sim 'finger' event fired by the hero (ev.last is set): pop the charge, flash a starburst at the fingertip (tip, world
   *  space) once per shot, and on the big moments start the colour wash. `maxRank` = the hero holds Finger Beam at rank 5. */
  release(ev: Extract<SimEvent, { t: 'finger' }>, tip: THREE.Vector3, maxRank: boolean, fx: Fx): void {
    if (ev.fan !== 0) return; // one starburst per shot (a Scarlet Fan shot's three needles share the fingertip)
    this.stats.released++;
    const big = ev.evolved ? 1.25 : 1, seed = this.stats.released * 2.399;
    this.add(1, tip, STAR_LIFE, 0.8 * big, 2.5 * big, seed, ev.evolved ? 8 : 6, 1);
    this.add(0, tip, POP_LIFE, 0.7 * big, 1.25 * big, 0, 0, 1);
    this.charge = 0; this.sparkAcc = 0;
    // a few sparks spat forward out of the fingertip (shared pool: follows the particle density setting)
    const L = Math.hypot(ev.ex - ev.x, ev.ez - ev.z) || 1, dx = (ev.ex - ev.x) / L, dz = (ev.ez - ev.z) / L;
    for (let k = 0; k < 3; k++) fx.particles.emitDir(tip.x, tip.y, tip.z, dx * (7 + k * 2) + (Math.random() - 0.5) * 2, Math.random() * 1.5, dz * (7 + k * 2) + (Math.random() - 0.5) * 2, 0xff8a96, 0.12, 0.12, 2.4);
    if (ev.last && (ev.evolved || maxRank)) { // the big moment: subtle crimson wash, one big flash in the Reduce flashing budget
      const k = FLASH.mode === 'minimal' ? 0 : FLASH.big() * (FLASH.mode === 'reduced' ? 0.5 : 1);
      if (k > 0.02) { this.washT = 0; this.washK = k * (ev.evolved ? 1 : 0.8); this.stats.washes++; }
    }
  }

  private add(kind: 0 | 1, at: THREE.Vector3, life: number, s0: number, s1: number, rot: number, n: number, k: number): void {
    const it = this.items.reduce((a, b) => (!b.on ? (a.on ? b : a) : !a.on ? a : b.t / b.life > a.t / a.life ? b : a));
    Object.assign(it, { kind, x: at.x, y: at.y, z: at.z, t: 0, life, s0, s1, rot, n, k, on: true });
  }

  /** per frame, after the hero pose is updated. tip = the hero's fingertip (world), null = no hero shown. */
  update(dt: number, sim: Sim, tip: THREE.Vector3 | null, camQ: THREE.Quaternion, fx: Fx): void {
    // charge level from the sim's own timer: 0 → 1 over the last CHARGE_T s before the next shot
    const next = sim.fingerNextIn();
    const target = this.holdCharge ?? (tip && Number.isFinite(next) && next < CHARGE_T && sim.state === 'playing' ? 1 - next / CHARGE_T : 0);
    this.charge = target >= this.charge ? target : Math.max(target, this.charge - dt * 6); // a charge with no shot (target lost) fades out
    this.spin += dt * 2.2;
    let n = 0;
    const C = this.col.array as Float32Array, S = this.shp.array as Float32Array, dim = FLASH.dim, fl = FLASH.flick(1);
    const put = (x: number, y: number, z: number, size: number, rot: number, orbW: number, starW: number, spikes: number, col: THREE.Color, k: number) => {
      if (n >= CAP) return;
      _q.copy(camQ).multiply(_rq.setFromAxisAngle(_z, rot)); // camera-facing, spun in screen space
      _m.compose(_p.set(x, y, z), _q, _s.set(size, size, 1)); this.mesh.setMatrixAt(n, _m);
      C[n * 4] = col.r; C[n * 4 + 1] = col.g; C[n * 4 + 2] = col.b; C[n * 4 + 3] = k;
      S[n * 4] = orbW; S[n * 4 + 1] = starW; S[n * 4 + 2] = 0; S[n * 4 + 3] = spikes; n++;
    };
    const c = this.charge;
    if (tip && c > 0.01) {
      const e = Math.pow(c, 0.8), flick = 1 + fl * 0.08 * Math.sin(this.spin * 23);
      // the orb: swells from a hot pinpoint to a hand-sized glow
      put(tip.x, tip.y, tip.z, (0.22 + 0.68 * e) * flick, 0, 1, 0, 0, HALO, (0.6 + 1.1 * e) * SKILL.core * 2 * dim);
      // a wide faint outer glow, so the charge still reads from the game camera
      put(tip.x, tip.y, tip.z, (0.5 + 1.4 * e) * flick, 0, 0.35, 0, 0, HALO, (0.25 + 0.45 * e) * SKILL.core * 2 * dim);
      // faint spike rays turning through it (they sharpen as it peaks)
      put(tip.x, tip.y, tip.z, 0.4 + 1.0 * e, this.spin, 0, 1, 6, STAR, Math.pow(c, 1.6) * 0.8 * SKILL.core * 2 * dim);
      // sparks drawn in toward the fingertip
      this.sparkAcc += dt * 40 * c;
      while (this.sparkAcc >= 1) {
        this.sparkAcc -= 1;
        const a = Math.random() * 6.283, b = Math.random() * 2 - 1, d = 0.45 + Math.random() * 0.25, h = Math.sqrt(1 - b * b);
        const ox = Math.cos(a) * h * d, oy = b * d, oz = Math.sin(a) * h * d;
        fx.particles.emitDir(tip.x + ox, tip.y + oy, tip.z + oz, -ox * 4.5, -oy * 4.5, -oz * 4.5, 0xff4a62, 0.07, 0.2, 2.6);
      }
    }
    for (const it of this.items) {
      if (!it.on) continue;
      it.t = this.hold ?? it.t + dt;
      if (it.t > it.life) { it.on = false; continue; }
      const u = it.t / it.life, fade = (1 - u) * (1 - u);
      if (it.kind === 1) put(it.x, it.y, it.z, it.s0 + (it.s1 - it.s0) * Math.sqrt(u), it.rot, 0.25, 1, it.n, STAR, fade * 1.6 * SKILL.core * 2 * dim * FLASH.L.white);
      else put(it.x, it.y, it.z, it.s0 + (it.s1 - it.s0) * u, 0, 1, 0, 0, HALO, fade * 1.4 * SKILL.core * 2 * dim);
    }
    // per-instance rotation goes through the matrix (shape.z unused: kept for the material's API)
    this.mesh.count = n;
    if (n) { this.mesh.instanceMatrix.needsUpdate = true; this.col.needsUpdate = true; this.shp.needsUpdate = true; }
    // colour wash envelope
    if (this.washT >= 0) {
      this.washT = this.hold ?? this.washT + dt;
      const w = this.washT;
      this.wash = w > WASH_LIFE ? 0 : this.washK * WASH_PEAK * (w < WASH_RISE ? w / WASH_RISE : Math.pow(1 - (w - WASH_RISE) / (WASH_LIFE - WASH_RISE), 1.5));
      if (w > WASH_LIFE && this.hold === null) this.washT = -1;
    } else this.wash = 0;
    if (this.washU) this.washU.value = this.wash;
  }

  clear(): void { for (const it of this.items) it.on = false; this.mesh.count = 0; this.charge = 0; this.wash = 0; this.washT = -1; if (this.washU) this.washU.value = 0; }
}
